// Small display-only helpers derived from the CV data. No data-shape changes.

import type { ExperienceItem } from '../../data/cv'
import type { CondensedRole } from './EarlierExperience'

/** Pull the two 4-digit years from a "MM/YYYY – MM/YYYY" period string. */
export function yearRange(period: string): { start: string; end: string } {
  const years = period.match(/\d{4}/g) ?? []
  const start = years[0] ?? ''
  const end = years[1] ?? years[0] ?? ''
  return { start, end }
}

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

/** Format a token as "Mmm YYYY" (or just "YYYY" when no month is present). */
function formatMonthYear(token: string): string {
  const m = token.match(/(?:(\d{1,2})\/)?(\d{4})/)
  if (!m) return token
  const year = m[2]
  if (!m[1]) return year
  const mi = parseInt(m[1], 10) - 1
  return MONTHS[mi] ? `${MONTHS[mi]} ${year}` : year
}

/**
 * Formatted start/end endpoints for a period, including month names when the
 * source has them (e.g. "Nov 2025" / "Mar 2026"). `same` is true when both
 * endpoints render identically (so the spine can show a single label).
 */
export function periodEndpoints(period: string): {
  start: string
  end: string
  same: boolean
} {
  const tokens = period.match(/(\d{1,2}\/)?\d{4}/g) ?? []
  const start = formatMonthYear(tokens[0] ?? '')
  const end = formatMonthYear(tokens[tokens.length - 1] ?? tokens[0] ?? '')
  return { start, end, same: start === end }
}

/** Sort key for a period string: the end date as YYYYMM (falles back to start). */
export function periodSortKey(period: string): number {
  // Grab all MM/YYYY (or YYYY) tokens; use the last one as the end.
  const tokens = period.match(/(\d{1,2}\/)?\d{4}/g) ?? []
  const end = tokens[tokens.length - 1] ?? ''
  const m = end.match(/(?:(\d{1,2})\/)?(\d{4})/)
  if (!m) return 0
  const month = m[1] ? parseInt(m[1], 10) : 12
  const year = parseInt(m[2], 10)
  return year * 100 + month
}

/** Parse a "MM/YYYY" or "YYYY" token into {month, year}; month defaults sensibly. */
function parseMonthYear(
  token: string,
  isEnd: boolean,
): { month: number; year: number } | null {
  const m = token.match(/(?:(\d{1,2})\/)?(\d{4})/)
  if (!m) return null
  // Start of a year defaults to Jan; end of a year defaults to Dec.
  const month = m[1] ? parseInt(m[1], 10) : isEnd ? 12 : 1
  return { month, year: parseInt(m[2], 10) }
}

/**
 * Human-readable role duration from a period string, always rounding UP so a
 * partial month/year counts as a whole one (e.g. "6 mos", "1 yr", "2 yrs 3 mos").
 */
export function roleDuration(period: string): string {
  const tokens = period.match(/(\d{1,2}\/)?\d{4}/g) ?? []
  if (tokens.length === 0) return ''
  const start = parseMonthYear(tokens[0], false)
  const end = parseMonthYear(tokens[tokens.length - 1] ?? tokens[0], true)
  if (!start || !end) return ''

  // Elapsed months between endpoints (Jun 2023 -> Jun 2024 = 12 months = 1 yr).
  let months = (end.year - start.year) * 12 + (end.month - start.month)
  if (months < 1) months = 1

  if (months < 12) return `${months} mo${months === 1 ? '' : 's'}`

  // A year or more: show whole years plus any leftover months, e.g.
  // 12 -> "1 yr", 15 -> "1 yr 3 mos", 24 -> "2 yrs".
  const years = Math.floor(months / 12)
  const remainder = months % 12
  const yr = `${years} yr${years === 1 ? '' : 's'}`
  if (remainder === 0) return yr
  return `${yr} ${remainder} mo${remainder === 1 ? '' : 's'}`
}

/** Strip any "· On-site / Remote" suffix, returning [place, mode]. */
export function splitLocation(location?: string): {
  place: string
  mode?: string
} {
  if (!location) return { place: '' }
  const [place, mode] = location.split('·').map((s) => s.trim())
  return { place, mode }
}

/** Two-digit index label, e.g. 1 -> "01". */
export function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

/**
 * Condense a full experience entry into a compact one-line role for the
 * "Earlier Experience" list — used when demoting older detailed roles.
 */
export function condenseRole(item: ExperienceItem): CondensedRole {
  const { start, end, same } = periodEndpoints(item.period)
  const period = same ? end : `${start} – ${end}`

  // First sentence of the first summary paragraph, kept to a single line.
  const firstParagraph = item.summary?.[0] ?? ''
  let note = firstParagraph.split(/(?<=\.)\s/)[0]?.trim() || undefined
  if (note && note.length > 150) note = note.slice(0, 147).trimEnd() + '…'

  return {
    company: item.company,
    role: item.title,
    period,
    note,
    stack: item.stack?.slice(0, 5),
  }
}

/** Derive a short lettermark from a company name when one isn't supplied. */
export function deriveMark(company: string): string {
  const cleaned = company
    .replace(/[—–-].*$/, '') // drop trailing "— Health Care" etc.
    .replace(/[^A-Za-z0-9 ]/g, ' ')
    .trim()
  const words = cleaned.split(/\s+/).filter(Boolean)
  const stop = new Set(['for', 'and', 'the', 'of', 'group'])
  const significant = words.filter((w) => !stop.has(w.toLowerCase()))
  const source = significant.length ? significant : words
  if (source.length === 1) return source[0].slice(0, 2).toUpperCase()
  return source
    .slice(0, 3)
    .map((w) => w[0].toUpperCase())
    .join('')
}
